import React, { useState, useEffect, Suspense } from 'react';
import { Lock } from 'lucide-react';
import { Hero } from './components/Hero';
import { ProductVideo } from './components/ProductVideo';
import { Features } from './components/Features';
import { HowItWorks } from './components/HowItWorks';
import { LandingTrust } from './components/LandingTrust';
import { LandingCta } from './components/LandingCta';
import { Footer } from './components/Footer';
import { Button } from './components/Button';
import { LandingNav } from './components/LandingNav';
import { SampleReport } from './components/SampleReport';
import { LandingFaq } from './components/LandingFaq';
import './landing.css';
import { useTranslation } from 'react-i18next';
import { AppShell } from './components/AppShell';
import { SEO } from './components/SEO';
import { useUserData } from './hooks/useUserData';
import type { AppContextValue } from './hooks/useAppContext';
import { auth, db } from './firebase';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { isNative } from './utils/platform';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';

const LoginModal = React.lazy(() => import('./components/LoginModal').then(module => ({ default: module.LoginModal })));
const VerificationPending = React.lazy(() => import('./components/VerificationPending').then(module => ({ default: module.VerificationPending })));
const AuthAction = React.lazy(() => import('./components/AuthAction').then(module => ({ default: module.AuthAction })));
const Privacy = React.lazy(() => import('./pages/Privacy').then(module => ({ default: module.Privacy })));
const Terms = React.lazy(() => import('./pages/Terms').then(module => ({ default: module.Terms })));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage').then(module => ({ default: module.DashboardPage })));
const ParentDetailPage = React.lazy(() => import('./pages/ParentDetailPage').then(module => ({ default: module.ParentDetailPage })));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage').then(module => ({ default: module.ReportsPage })));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage').then(module => ({ default: module.SettingsPage })));

import { onAuthStateChanged, User } from 'firebase/auth';
import { Routes, Route, useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';

const PageSpinner: React.FC = () => (
    <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
    </div>
);

interface ProtectedLayoutProps {
    user: User | null;
    authLoading: boolean;
}

const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({ user, authLoading }) => {
    const { userData, loading: profileLoading } = useUserData(user);

    if (authLoading || profileLoading) {
        return <PageSpinner />;
    }

    if (!user || !user.emailVerified) {
        return <Navigate to="/" replace />;
    }

    const context: AppContextValue = { user, userData };

    return (
        <AppShell user={user}>
            <Outlet context={context} />
        </AppShell>
    );
};

const App: React.FC = () => {
    const { t } = useTranslation();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [loginInitialMode, setLoginInitialMode] = useState<'login' | 'signup'>('login');
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAppLocked, setIsAppLocked] = useState(isNative && localStorage.getItem('requireFaceID') === 'true');
    const navigate = useNavigate();
    const location = useLocation();

    const handleBiometricUnlock = async () => {
        try {
            const info = await BiometricAuth.checkBiometry();
            if (info.isAvailable) {
                await BiometricAuth.authenticate({
                    reason: 'Unlock ParentsCheck',
                    cancelTitle: 'Cancel'
                });
                setIsAppLocked(false);
            } else {
                setIsAppLocked(false);
            }
        } catch (error) {
            console.error('Biometric Auth Error:', error);
        }
    };

    useEffect(() => {
        if (isAppLocked) {
            handleBiometricUnlock();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAppLocked]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setIsLoading(false);
            if (currentUser) {
                setIsLoginModalOpen(false);
                const userData = {
                    email: currentUser.email || null,
                    displayName: currentUser.displayName || null,
                    createdAt: serverTimestamp(),
                };

                const ensureProfile = runTransaction(db, async tx => {
                    const ref = doc(db, 'users', currentUser.uid);
                    const existing = await tx.get(ref);
                    if (!existing.exists()) tx.set(ref, userData);
                });

                void ensureProfile.then(() => {
                    if (isNative && auth.currentUser?.uid === currentUser.uid) {
                        import('./utils/push-notifications').then(({ registerPushNotifications }) => {
                            registerPushNotifications(currentUser, (data) => {
                                if (typeof data.parentId === 'string' && data.parentId) {
                                    navigate(`/parent/${data.parentId}`);
                                }
                            });
                        });
                        import('./services/revenuecat').then(({ initializeRevenueCat }) => {
                            initializeRevenueCat(currentUser.uid);
                        });
                    }
                }).catch(err => console.error('Error ensuring user doc:', err));
            } else {
                if (isNative) {
                    import('./utils/push-notifications').then(({ clearPushOnAuthLoss }) => { if (!auth.currentUser) return clearPushOnAuthLoss(); }).catch(() => console.warn('[Push] Cleanup failed'));
                    import('./services/revenuecat').then(({ logoutRevenueCat }) => {
                        logoutRevenueCat();
                    });
                }
            }
        });

        const timeout = setTimeout(() => {
            setIsLoading(false);
        }, 5000);

        return () => {
            unsubscribe();
            clearTimeout(timeout);
        };
    }, []);

    const openLogin = (mode: 'login' | 'signup') => {
        setLoginInitialMode(mode);
        setIsLoginModalOpen(true);
    };

    const isLegalPage = ['/privacy', '/terms', '/terms-of-service'].includes(location.pathname);
    const showLandingChrome = location.pathname === '/' && !user && !isNative && !isLegalPage;

    if (isLoading) {
        return <PageSpinner />;
    }

    if (isAppLocked) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 animate-in fade-in duration-300">
                <div className="w-20 h-20 rounded-2xl bg-violet-100 flex items-center justify-center mb-6 shadow-sm border-2 border-white">
                    <Lock className="w-10 h-10 text-violet-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2 font-display">App Locked</h1>
                <p className="text-slate-500 text-center mb-8">Unlock with Face ID to access ParentsCheck.</p>

                <Button onClick={handleBiometricUnlock} size="lg" className="w-full max-w-xs shadow-lg shadow-violet-200">
                    Unlock Now
                </Button>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${showLandingChrome || (!user && location.pathname === '/') ? 'landing-site' : 'bg-slate-50'}`}>
            {showLandingChrome && <LandingNav onLogin={openLogin} />}

            <main id="landing-content" tabIndex={-1}>
                <Suspense fallback={<PageSpinner />}>
                    <Routes>
                        <Route path="/" element={
                            user ? (
                                !user.emailVerified ? (
                                    <VerificationPending user={user} onVerified={() => window.location.reload()} />
                                ) : (
                                    <Navigate to="/dashboard" replace />
                                )
                            ) : (
                                <>
                                    <SEO
                                        title={t('landingSeo.title')}
                                        description={t('landingSeo.description')}
                                    />
                                    <Hero
                                        onCtaClick={() => openLogin('signup')}
                                    />
                                    <HowItWorks />
                                    <SampleReport />
                                    <Features />
                                    <ProductVideo />
                                    <LandingTrust />
                                    <LandingFaq />
                                    <LandingCta onCtaClick={() => openLogin('signup')} />
                                </>
                            )
                        } />

                        <Route element={<ProtectedLayout user={user} authLoading={false} />}>
                            <Route path="/dashboard" element={<DashboardPage />} />
                            <Route path="/parent/:parentId" element={<ParentDetailPage />} />
                            <Route path="/reports" element={<ReportsPage />} />
                            <Route path="/settings" element={<SettingsPage />} />
                        </Route>

                        <Route path="/auth/action" element={<AuthAction onComplete={() => navigate('/')} />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </Suspense>
            </main>

            {showLandingChrome && <Footer />}

            <Suspense fallback={null}>
                {isLoginModalOpen && (
                    <LoginModal
                        isOpen={isLoginModalOpen}
                        onClose={() => { setIsLoginModalOpen(false); setLoginInitialMode('login'); }}
                        onLoginSuccess={() => setIsLoginModalOpen(false)}
                        initialMode={loginInitialMode}
                    />
                )}
            </Suspense>
        </div>
    );
};

export default App;
